import { ConvexError } from 'convex/values';
import { parseConvexClientFailure } from './app-error-context';
import { redactUserBlockingPagePath } from './user-blocking-page-path';

/**
 * User-blocking failures (#6012): a send, a new task, an upload or a page
 * that failed in front of the user. Each one is captured as one PostHog
 * event, which a PostHog destination posts to Discord #alerting, deduped per
 * `alert_key` with a re-alert window. Properties carry error codes, function
 * names and ids only, never message text, so no user content leaves the page.
 */
export const USER_BLOCKING_FAILURE_EVENT = 'user_blocking_failure';

export type UserBlockingSurface =
  | 'send'
  | 'create_thread'
  | 'upload'
  | 'page_crash';

/** Name of the error the Lune-only crash probe throws (see keel-alert-probe). */
export const USER_BLOCKING_ALERT_PROBE_ERROR_NAME =
  'UserBlockingAlertProbeError';

export interface UserBlockingFailure {
  surface: UserBlockingSurface;
  /** Convex function (`module:export`) or client operation that failed. */
  operation?: string;
  error: unknown;
  threadId?: string | null;
}

export type UserBlockingErrorKind =
  | 'convex_error'
  | 'server_error'
  | 'client_error';

export interface UserBlockingFailureProperties {
  surface: UserBlockingSurface;
  operation: string;
  error_kind: UserBlockingErrorKind;
  /** A backend code (`ATTACHMENT_VFS_REFERENCE_UNAVAILABLE`) or error class. */
  error_code: string;
  /** Hash of the normalized message, so uncoded errors still group apart. */
  error_fingerprint?: string;
  request_id?: string;
  thread_id?: string;
  workspace_slug: string;
  page_path?: string;
  page_url?: string;
  alert_key: string;
  probe?: true;
}

const CODE_PATTERN = /^[A-Z][A-Z0-9_]{2,79}$/;
const ERROR_CLASS_PATTERN = /^[A-Za-z][A-Za-z0-9]{0,39}$/;
const SAFE_OPERATION_PATTERN = /^[A-Za-z0-9_./:-]{1,180}$/;
const SAFE_ID_PATTERN = /^[A-Za-z0-9_-]{1,80}$/;
const SAFE_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,79}$/;
const SAFE_DIGEST_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

function readErrorCode(data: unknown): string | undefined {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return CODE_PATTERN.test(trimmed) ? trimmed : undefined;
  }
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code?: unknown }).code;
    return typeof code === 'string' && CODE_PATTERN.test(code)
      ? code
      : undefined;
  }
  return undefined;
}

/** FNV-1a over the message with numbers and ids blanked; 8 hex chars. */
export function userBlockingErrorFingerprint(message: string): string {
  const normalized = message
    .replace(/\[Request ID:[^\]]*\]/g, '')
    .replace(/[A-Za-z0-9_-]*\d[A-Za-z0-9_-]*/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  let hash = 0x811c9dc5;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : '';
}

function classifyError(error: unknown): {
  kind: UserBlockingErrorKind;
  code: string;
  fingerprint?: string;
} {
  const message = errorMessage(error);
  if (error instanceof ConvexError) {
    const code = readErrorCode((error as { data?: unknown }).data);
    return code
      ? { kind: 'convex_error', code }
      : {
          kind: 'convex_error',
          code: 'CONVEX_ERROR',
          fingerprint: userBlockingErrorFingerprint(
            JSON.stringify((error as { data?: unknown }).data ?? message) ?? ''
          ),
        };
  }
  if (parseConvexClientFailure(message)) {
    return { kind: 'server_error', code: 'SERVER_ERROR' };
  }
  const name = error instanceof Error ? error.name : '';
  return {
    kind: 'client_error',
    code: ERROR_CLASS_PATTERN.test(name) ? name : 'UnknownError',
    ...(message ? { fingerprint: userBlockingErrorFingerprint(message) } : {}),
  };
}

export interface UserBlockingFailureLocation {
  pathname?: string | null;
  origin?: string | null;
  /** The workspace the page is in, when the boundary has resolved it. */
  workspaceSlug?: string | null;
}

function crashDigest(error: unknown): string | undefined {
  const digest = (error as { digest?: unknown } | null)?.digest;
  return typeof digest === 'string' && SAFE_DIGEST_PATTERN.test(digest)
    ? digest
    : undefined;
}

export function createUserBlockingAlertProbeError(): Error {
  const error = new Error('Keel user-blocking alert probe');
  error.name = USER_BLOCKING_ALERT_PROBE_ERROR_NAME;
  return error;
}

export function isUserBlockingAlertProbeError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === USER_BLOCKING_ALERT_PROBE_ERROR_NAME
  );
}

export function buildUserBlockingFailureProperties(
  failure: UserBlockingFailure,
  location: UserBlockingFailureLocation = {}
): UserBlockingFailureProperties {
  const { kind, code, fingerprint } = classifyError(failure.error);
  const parsed = parseConvexClientFailure(errorMessage(failure.error));
  const operation =
    failure.operation && SAFE_OPERATION_PATTERN.test(failure.operation)
      ? failure.operation
      : (parsed?.convexFunction ?? 'unknown');
  const page = redactUserBlockingPagePath(
    location.pathname,
    location.workspaceSlug
  );
  const workspaceSlug = [location.workspaceSlug, page.workspaceSlug].find(
    (slug): slug is string =>
      typeof slug === 'string' && SAFE_SLUG_PATTERN.test(slug)
  );
  const threadId =
    failure.threadId && SAFE_ID_PATTERN.test(failure.threadId)
      ? failure.threadId
      : undefined;
  const origin =
    typeof location.origin === 'string' &&
    /^https?:\/\/[A-Za-z0-9.:-]+$/.test(location.origin)
      ? location.origin
      : undefined;
  const probe = isUserBlockingAlertProbeError(failure.error);
  return {
    surface: failure.surface,
    operation,
    error_kind: kind,
    error_code: code,
    ...(fingerprint ? { error_fingerprint: fingerprint } : {}),
    ...(parsed?.requestId ? { request_id: parsed.requestId } : {}),
    ...(threadId ? { thread_id: threadId } : {}),
    workspace_slug: workspaceSlug ?? 'unknown',
    ...(page.path ? { page_path: page.path } : {}),
    // A path with a segment blanked out would link to the wrong page.
    ...(page.path && page.linkable && origin
      ? { page_url: `${origin}${page.path}` }
      : {}),
    alert_key: [
      failure.surface,
      operation,
      code,
      fingerprint,
      // Unrelated crashes share Next's generic message: split them by route
      // and by the render digest Next attaches.
      ...(failure.surface === 'page_crash'
        ? [page.path?.split('/')[2], crashDigest(failure.error)]
        : []),
    ]
      .filter(Boolean)
      .join(':'),
    ...(probe ? { probe: true as const } : {}),
  };
}
